import { useEffect, useState } from "react";
import { backend } from "@/services/backend";
import { UserProfileFullData } from "@/services/backend";
import { useCollectionContract } from "./useCollectionContract";
import { useTonConnect } from "./useTonConnect";
import { requestProfilePrice } from "./useCollectionContract";
import { sleep } from "../utils/common";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { actions, RootState } from "@/state/reducer";
import { useOwnerWhitelist } from "./useOwnerWhitelist";

interface UseWhitelistStatusProps {
  enabled: boolean;
  wallet: string | null;
  collection: string;
}

export function useWhitelistStatus({
  enabled,
  wallet,
  collection,
}: UseWhitelistStatusProps) {
  const snackbar = useSnackbar();
  const balance = useSelector((state: RootState) => state.balance);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [showWhitelistSuccessMessage, setShowWhitelistSuccessMessage] =
    useState<boolean>(false);
  const { connected, fetchBalance } = useTonConnect();
  const dispatch = useDispatch();
  const [isWhitelisted, setIsWhitelisted] = useState<boolean | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfileFullData | null>(
    null
  );
  const { whitelistTrigger } = useSelector((state: RootState) => state);
  const [showWalletConnectionModal, setShowWalletConnectionModal] =
    useState<boolean>(false);
  const { requestProfile, updateWhitelist, updateWhitelistMultiple } =
    useCollectionContract();

  useEffect(() => {
    if (connected) {
      setShowWalletConnectionModal(false);
    }
  }, [connected]);

  useEffect(() => {
    const fetchWhitelistData = async () => {
      if (!enabled || !wallet || !collection) {
        setIsWhitelisted(null);
        setUserProfile(null);
        return;
      }

      try {
        const [status, profile] = await Promise.all([
          backend.getWhitelistStatus(wallet, collection),
          backend.getUserCollectionProfile(wallet, collection),
        ]);
        setIsWhitelisted(status);
        setUserProfile(profile);
      } catch (error) {
        console.error("Failed to fetch whitelist or profile data:", error);
        setIsWhitelisted(null);
        setUserProfile(null);
      } finally {
      }
    };

    fetchWhitelistData();
  }, [enabled, wallet, collection, whitelistTrigger]);

  const handleRequestProfile = async () => {
    if (!wallet) {
      snackbar.enqueueSnackbar("Please connect your wallet first", {
        SnackbarProps: {
          onClick: () => {
            snackbar.closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 1000,
      });
      setShowWalletConnectionModal(true);
      return;
    }
    if (!collection) return false;

    // Fetch fresh balance before checking
    /* const currentBalance = await fetchBalance();
    if (!currentBalance || Number(currentBalance) < Number(requestProfilePrice)) {
      snackbar.enqueueSnackbar("Insufficient balance", {
        variant: "error",
        autoHideDuration: 1000,
      });
      return;
    } */
    const result = await requestProfile(collection, () =>
      dispatch(actions.whitelistTrigger(wallet))
    );
    setIsLoadingProfile(true);
    if (result) {
      await sleep(10000);
      let updatedProfile = await backend.getUserCollectionProfile(
        wallet,
        collection
      );
      console.log("1st updation:", updatedProfile);
      if (!updatedProfile || updatedProfile.is_deployed === false) {
        await sleep(10000);
        updatedProfile = await backend.getUserCollectionProfile(
          wallet,
          collection
        );
        console.log("2nd updation:", updatedProfile);
      }
      if (!updatedProfile || updatedProfile.is_deployed === false) {
        await sleep(10000);
        updatedProfile = await backend.getUserCollectionProfile(
          wallet,
          collection
        );
        console.log("3rd updation:", updatedProfile);
      }
      setUserProfile(updatedProfile);
      setIsLoadingProfile(false);
      if (updatedProfile && updatedProfile.is_deployed) {
        setShowWhitelistSuccessMessage(true);
      }
      return;
    }
    setIsLoadingProfile(false);
    return;
  };

  return {
    isWhitelisted,
    isLoadingProfile,
    userProfile,
    requestProfile: handleRequestProfile,
    updateWhitelist,
    updateWhitelistMultiple,
    showWalletConnectionModal,
    setShowWalletConnectionModal,
    showWhitelistSuccessMessage,
    setShowWhitelistSuccessMessage,
  };
}
